import { useQuery } from '@tanstack/react-query';
import { userService } from '@/features/auth/api/user.service';
import { profileService } from '@/shared/api/services/profile.service';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import type { UsageResponse, UsageStatsResponse } from '@/types/user';
import type { PaywallPayload } from '@/shared/lib/api';

const USAGE_KEY = 'usage';

/**
 * Fetch the full profile bundle (user + tokens + subscription + recent
 * activity) used by the settings dashboard. Keyed under `cacheKeys.profile`
 * to leave `cacheKeys.user` free for the canonical `User` entity (Wave A3 —
 * STATE_POLICY: server data lives in Query, auth.store keeps only token + UI
 * flags). Auth flows still seed the User into `cacheKeys.user` via
 * `profileService.applyProfileData`.
 */
export function useProfile() {
  return useQuery({
    queryKey: cacheKeys.profile,
    queryFn: async () => {
      return profileService.fetchProfile();
    },
  });
}

/**
 * Server-driven soft low-balance nudge (`user.tokens.nudge`). Non-null ONLY
 * while the wallet is in the server's "running low" band — the server owns
 * the threshold, the client never re-derives it. Same shape as a
 * PaywallPayload (localized title/body + actions). Returns `null` when no
 * nudge is active or the profile hasn't loaded.
 *
 * Drives the nav low-balance popover (TokenBalanceIndicator) and the
 * non-blocking post-render nudge — both consume this single source so they
 * can never disagree with the server's band.
 */
export function useTokenNudge(): PaywallPayload | null {
  const { data } = useProfile();
  return data?.tokens?.nudge ?? null;
}

/**
 * Server-driven legacy-token flag (`user.tokens.is_legacy`). True for accounts
 * that keep the legacy token economy (balance > 0, past token purchases, or a
 * grandfathered subscription) — the ONLY accounts that may see token numerics,
 * nudges, and the token storefront post-pivot. Free/pack users return false and
 * must never see the word "token". Never derive client-side from balance.
 *
 * `undefined` until the profile seeds — callers treat undefined as "not yet
 * known" (hide token chrome rather than flash it).
 */
export function useIsLegacyTokenUser(): boolean | undefined {
  const { data } = useProfile();
  return data?.tokens?.is_legacy;
}

/**
 * Server-localized free-tier counter label (`user.free_tier.designs_label`),
 * e.g. "5 free designs left". Null while the free tier is inactive (the user is
 * legacy or holds a pack). Clients render verbatim — never compose it.
 */
export function useFreeTierLabel(): string | null {
  const { data } = useProfile();
  return data?.free_tier?.designs_label ?? null;
}

/**
 * Fetch user usage information
 * Shows token usage, render counts, etc.
 */
export function useUsage() {
  return useQuery({
    queryKey: [USAGE_KEY],
    queryFn: async (): Promise<UsageResponse> => {
      return userService.getUsage();
    },
    staleTime: 2 * 60 * 1000, // 2 minutes - usage changes more frequently
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Fetch detailed usage statistics
 * Shows breakdown by feature, time period, etc.
 */
export function useUsageStats() {
  return useQuery({
    queryKey: [USAGE_KEY, 'stats'],
    queryFn: async (): Promise<UsageStatsResponse> => {
      return userService.getUsageStats();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - stats update less frequently
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}
